/**
 * /stock/favorites — the customer's favorited stock vehicles, rendered as the
 * same vehicle cards used on the catalog (not bare vehicle ids).
 *
 * Flow: load the favorite set from the BFF. Each row carries an embedded
 * `vehicle` summary (English name, price, thumbnail, effective ourStatus,
 * public vehicle UUID) so the card renders without a per-vehicle round-trip:
 *   - not sold + public UUID → the card links to the publicly available
 *     vehicle detail page (/inventory/<public vehicle UUID>).
 *   - ourStatus === 'sold' → the stale favorite is removed automatically and
 *     is not rendered.
 * FALLBACK (upstream not yet deployed → `vehicle` missing): resolve the
 * vehicle's BASIC detail per row as before. A sold / withdrawn / not-found
 * vehicle (Stock Core 404 / 409) degrades to a non-clickable "SOLD" card
 * carrying only the remove button so the customer can clear the stale
 * favorite (see docs/specs/STOCK_CORE_FEATURES_FAVORITES_INQUIRIES_BUY_REQUESTS.md §2.4).
 */
import { useEffect, useState } from 'react';
import { portalApi, PortalApiError } from '../lib/apiClient';
import { fetchStockDetail, toHalfWidth } from '../lib/stockApi';
import { getStockVehicleUrl } from '../lib/featureFlags';
import { FavoriteToggleButton } from '../components/stock/FavoriteToggleButton';
import { StockListCard } from '../components/stock/StockListCard';
import { BRAND } from '../components/layout/designTokens';
import type { StockDetailResponse, StockListItem } from '../lib/stockTypes';

/**
 * Embedded vehicle summary from GET /api/portal/stock/favorites (shared API
 * contract — see packages/shared/src/stock/favorites.ts). Favorites-only:
 * deliberately NOT part of stockTypes' public StockListItem allowlist.
 */
interface FavoriteVehicleSummary {
  make: string | null;
  model: string | null;
  makerEn: string | null;
  carnameEn: string | null;
  grade: string | null;
  year: number | null;
  registration: number | null;
  mileage: number | null;
  listPrice: number | null;
  thumbnailUrl: string | null;
  ourStatus: string | null;
  vendorVehicleId: string | null;
}

interface FavoriteItem {
  favoriteId: string;
  vehicleId: string;
  favoritedAt: string;
  /** Missing on responses from an older BFF / Stock Core deployment. */
  vehicle?: FavoriteVehicleSummary | null;
}

interface FavoritesResponse {
  ok: boolean;
  data: { items: FavoriteItem[]; nextCursor: string | null };
}

/** A favorite plus its resolved vehicle summary (or a withdrawn / error marker). */
interface FavoriteCard extends FavoriteItem {
  status: 'ready' | 'unavailable' | 'error';
  /** Card-renderable summary; null unless `status === 'ready'`. */
  item: StockListItem | null;
  /** Public cars-for-sale detail link (never set when sold or UUID is absent). */
  href: string | null;
  /** Effective ourStatus === 'sold' → SOLD overlay, no link. */
  sold: boolean;
}

/**
 * Project the embedded favorites vehicle summary onto the StockListItem shape
 * the card renders. Title prefers the English fields ([makerEn, carnameEn]),
 * falling back to the Japanese [make, model] when both EN fields are empty.
 */
function summaryToCardItem(vehicleId: string, v: FavoriteVehicleSummary): StockListItem {
  const useEn = Boolean(v.makerEn?.trim()) || Boolean(v.carnameEn?.trim());
  return {
    vehicleId,
    maker: useEn ? (v.makerEn?.trim() ?? null) : v.make,
    carname: useEn ? (v.carnameEn?.trim() ?? null) : v.model,
    grade: v.grade ? toHalfWidth(v.grade.trim()) : null,
    registration: v.registration ?? v.year,
    mileage: v.mileage,
    listPrice: v.listPrice,
    bodytypeLabel: null,
    thumbnailUrl: v.thumbnailUrl,
    auctionGrade: null,
    inspectionStatus: null,
    updatedAt: null,
    isFavorited: true,
  };
}

/**
 * Project a stock-detail response (basic or full) onto the StockListItem shape
 * the catalog card renders. The favorites detail fetch never requests `full`,
 * so this is basic in practice; the full branch is defensive.
 */
function detailToCardItem(res: StockDetailResponse): StockListItem {
  const d = res.data;
  if (d.accessLevel === 'full') {
    const f = d.data;
    return {
      vehicleId: f.vehicleId,
      maker: f.basic.maker,
      carname: f.basic.carname,
      grade: f.basic.grade,
      registration: f.basic.registration,
      mileage: f.specs.mileage,
      listPrice: f.basic.listPrice,
      bodytypeLabel: f.specs.bodytypeLabel,
      thumbnailUrl: f.images[0]?.url ?? null,
      auctionGrade: f.inspection.aisScoreTotal,
      inspectionStatus: null,
      updatedAt: f.meta.aucnetUpdatedAt,
      isFavorited: d.isFavorited,
    };
  }
  const b = d.data;
  return {
    vehicleId: b.vehicleId,
    maker: b.maker,
    carname: b.carname,
    grade: b.grade,
    registration: b.registration,
    mileage: b.mileage,
    listPrice: b.listPrice,
    bodytypeLabel: b.bodytypeLabel,
    thumbnailUrl: b.thumbnailUrl,
    auctionGrade: b.auctionGrade,
    inspectionStatus: null,
    updatedAt: null,
    isFavorited: d.isFavorited,
  };
}

export function StockFavoritesPage() {
  const [cards, setCards] = useState<FavoriteCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    setCards(null);
    try {
      const res = await portalApi<FavoritesResponse>('/api/portal/stock/favorites');
      const favs = res.data.items;
      if (favs.length === 0) {
        setCards([]);
        return;
      }
      // Preferred path: render straight from the embedded vehicle summary.
      // FALLBACK (older upstream → `vehicle` missing): resolve the vehicle
      // summary per row in parallel. A per-vehicle failure is isolated to
      // that card (withdrawn → "掲載終了"); it never fails the whole page.
      const resolved = await Promise.all(
        favs.map(async (fav): Promise<FavoriteCard | null> => {
          if (fav.vehicle) {
            const sold = fav.vehicle.ourStatus?.toLowerCase() === 'sold';
            if (sold) {
              try {
                await portalApi<void>(
                  `/api/portal/stock/favorites/${encodeURIComponent(fav.vehicleId)}`,
                  { method: 'DELETE' },
                );
              } catch {
                // Hiding a known-sold listing is the important user-facing
                // behavior. A later reload retries cleanup if deletion failed.
              }
              return null;
            }
            const href = fav.vehicle.vendorVehicleId
              ? getStockVehicleUrl(fav.vehicle.vendorVehicleId)
              : null;
            return {
              ...fav,
              status: 'ready',
              item: summaryToCardItem(fav.vehicleId, fav.vehicle),
              href,
              sold,
            };
          }
          try {
            const detail = await fetchStockDetail(fav.vehicleId);
            return {
              ...fav,
              status: 'ready',
              item: detailToCardItem(detail),
              href: null,
              sold: false,
            };
          } catch (err) {
            const code = err instanceof PortalApiError ? err.code : 'UNKNOWN';
            const withdrawn =
              code === 'PORTAL_STOCK_NOT_FOUND' || code === 'PORTAL_STOCK_UNAVAILABLE';
            return {
              ...fav,
              status: withdrawn ? 'unavailable' : 'error',
              item: null,
              href: null,
              sold: false,
            };
          }
        }),
      );
      setCards(resolved.filter((card): card is FavoriteCard => card !== null));
    } catch (err) {
      const code = err instanceof PortalApiError ? err.code : 'UNKNOWN';
      setError(code);
      setCards([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  /** Drop a card locally once its favorite is removed (no full reload). */
  function handleRemoved(vehicleId: string): void {
    setCards((prev) => (prev ? prev.filter((c) => c.vehicleId !== vehicleId) : prev));
  }

  return (
    <div style={{ color: BRAND.navy900 }}>
      <h1
        style={{
          margin: '0 0 1.5rem',
          fontSize: '1.875rem',
          fontWeight: 700,
          color: BRAND.navy900,
          letterSpacing: '-0.01em',
        }}
      >
        Favorites
      </h1>
      {cards === null ? (
        <section aria-busy="true" data-testid="stock-favorites-loading">
          <p style={{ color: BRAND.slate500, marginBottom: '1rem' }}>Loading…</p>
          <div className="fav-grid">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{ height: '18rem', background: BRAND.beige200, borderRadius: '0.5rem' }}
              />
            ))}
          </div>
        </section>
      ) : (
        <section>
          {error ? (
            <div
              role="alert"
              data-testid="stock-favorites-error"
              style={{
                color: '#7F1D1D',
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              Failed to load ({error}){' '}
              <button
                type="button"
                onClick={() => void load()}
                style={{
                  marginLeft: '0.5rem',
                  border: `1px solid ${BRAND.slate300}`,
                  background: BRAND.white,
                  padding: '0.25rem 0.75rem',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  color: BRAND.navy900,
                }}
              >
                Reload
              </button>
            </div>
          ) : null}
          {cards.length === 0 && !error ? (
            <p style={{ color: BRAND.slate700 }} data-testid="stock-favorites-empty">
              You don&apos;t have any favorite vehicles yet.
            </p>
          ) : null}
          {cards.length > 0 ? (
            <ul
              className="fav-grid"
              data-testid="stock-favorites-grid"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {cards.map((card) => (
                <li key={card.favoriteId}>
                  {card.status === 'ready' && card.item ? (
                    <StockListCard
                      item={card.item}
                      href={card.href}
                      sold={card.sold}
                      onFavoriteChanged={(vehicleId, favorited) => {
                        if (!favorited) handleRemoved(vehicleId);
                      }}
                    />
                  ) : (
                    <UnavailableFavoriteCard card={card} onRemoved={handleRemoved} />
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      )}
      <style>{`
        .fav-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
          gap: 1rem;
        }
      `}</style>
    </div>
  );
}

interface UnavailableFavoriteCardProps {
  card: FavoriteCard;
  onRemoved: (vehicleId: string) => void;
}

/**
 * Fallback card for a favorite whose vehicle could not be loaded — withdrawn /
 * not-found (`unavailable`) or a transient detail error (`error`). Carries the
 * remove button so a stale favorite can always be cleared.
 */
function UnavailableFavoriteCard({ card, onRemoved }: UnavailableFavoriteCardProps): JSX.Element {
  const withdrawn = card.status === 'unavailable';
  return (
    <article
      data-testid={`stock-favorite-unavailable-${card.vehicleId}`}
      style={{
        border: `1px solid ${BRAND.slate200}`,
        borderRadius: '0.5rem',
        background: BRAND.white,
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '18rem',
      }}
    >
      <div>
        <div
          style={{
            aspectRatio: '4 / 3',
            borderRadius: '0.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: withdrawn ? BRAND.white : BRAND.slate500,
            background: withdrawn ? BRAND.navy900 : BRAND.beige200,
            fontSize: withdrawn ? '1.25rem' : '0.85rem',
            fontWeight: withdrawn ? 700 : 400,
            letterSpacing: withdrawn ? '0.08em' : undefined,
            marginBottom: '0.75rem',
          }}
        >
          {withdrawn ? 'SOLD' : 'No image'}
        </div>
        <p style={{ margin: 0, fontWeight: 600, color: BRAND.navy900 }}>
          {withdrawn
            ? 'SOLD — this vehicle is no longer available'
            : 'Could not load vehicle information'}
        </p>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: BRAND.slate500 }}>
          Added: {new Date(card.favoritedAt).toLocaleString('en-US')}
        </p>
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
        <FavoriteToggleButton
          vehicleId={card.vehicleId}
          initialFavorited
          onChanged={(favorited) => {
            if (!favorited) onRemoved(card.vehicleId);
          }}
        />
      </div>
    </article>
  );
}
